"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabase";

export default function AdminRegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // パスワード確認
    if (password !== confirmPassword) {
      setError("パスワードが一致しません");
      return;
    }

    // パスワード強度チェック
    if (password.length < 6) {
      setError("パスワードは6文字以上で設定してください");
      return;
    }

    setIsLoading(true);

    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/admin/login`,
          data: {
            email_confirm: true,
          },
        },
      });

      if (authError) {
        console.error("Auth error:", authError);
        
        if (authError.message.includes("User already registered")) {
          setError("このメールアドレスは既に登録されています。ログインページからログインしてください。");
        } else if (authError.message.includes("invalid") || authError.message.includes("Invalid")) {
          setError("有効なメールアドレスを入力してください");
        } else if (authError.message.includes("Password")) {
          setError("パスワードの形式が正しくありません");
        } else if (authError.message.includes("Email rate limit exceeded")) {
          setError("メール送信の制限に達しました。しばらく待ってから再度お試しください。");
        } else {
          setError(`登録に失敗しました: ${authError.message}`);
        }
        return;
      }

      if (data?.user) {
        // メール確認が必要かどうかをチェック
        if (data.user.identities && data.user.identities.length === 0) {
          setError("このメールアドレスは既に登録されています。ログインページからログインしてください。");
          return;
        }

        // メール確認が必要な場合
        if (!data.session) {
          setSuccess(
            "登録リクエストを受け付けました。\n\n" +
            "※ Supabaseの設定でメール確認が有効になっている場合、確認メールが送信されます。\n" +
            "メール確認が不要な設定の場合は、すぐにログインできます。\n\n" +
            "5秒後にログインページに移動します..."
          );
          // 5秒後にログインページへ
          setTimeout(() => {
            navigate("/admin/login");
          }, 5000);
        } else {
          // メール確認不要の場合（即座にログイン可能）
          navigate("/admin/login", {
            state: { message: "登録が完了しました。ログインしてください。" },
          });
        }
      }
    } catch (err) {
      console.error("Register error:", err);
      setError("エラーが発生しました。もう一度お試しください。");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* ロゴ・タイトル */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 flex items-center justify-center bg-teal-500 rounded-2xl mx-auto mb-4 shadow-lg">
            <i className="ri-user-add-line text-white text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">新規管理者登録</h1>
          <p className="text-gray-600 text-sm">きらり保育園 管理システム</p>
        </div>

        {/* 登録フォーム */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleRegister} className="space-y-5">
            {/* エラーメッセージ */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-fill text-red-500 text-xl flex-shrink-0"></i>
                <p className="text-red-700 text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            {/* 成功メッセージ */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0"></i>
                <p className="text-green-700 text-sm whitespace-pre-line">{success}</p>
              </div>
            )}

            {/* 注意事項 */}
            {!success && !error && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-fill text-blue-500 text-xl flex-shrink-0"></i>
                  <div className="text-blue-700 text-sm">
                    <p className="font-medium mb-1">登録について</p>
                    <p className="text-xs">Supabaseの設定により、メール確認が必要な場合があります。その場合は確認メールをご確認ください。</p>
                  </div>
                </div>
              </div>
            )}

            {/* メールアドレス */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                メールアドレス
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={!!success}
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="admin@example.com"
                />
              </div>
            </div>

            {/* パスワード */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                パスワード
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={!!success}
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="6文字以上"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={!!success}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer disabled:cursor-not-allowed"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">※6文字以上で設定してください</p>
            </div>

            {/* パスワード確認 */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                パスワード（確認）
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={!!success}
                  className="w-full pl-11 pr-12 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="パスワードを再入力"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={!!success}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer disabled:cursor-not-allowed"
                >
                  <i className={showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {/* 登録ボタン */}
            <button
              type="submit"
              disabled={isLoading || !!success}
              className="w-full bg-teal-500 text-white font-semibold py-3 px-4 rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  登録中...
                </span>
              ) : success ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  登録完了
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-user-add-line"></i>
                  登録する
                </span>
              )}
            </button>
          </form>

          {/* ログインリンク */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-gray-600 text-sm mb-2">既にアカウントをお持ちの方</p>
            <a
              href="/admin/login"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer"
            >
              <i className="ri-login-box-line"></i>
              ログインはこちら
            </a>
          </div>
        </div>

        {/* フッター */}
        <p className="text-center text-gray-500 text-xs mt-6">
          © きらり保育園箕面 管理システム
        </p>
      </div>
    </div>
  );
}
